/*
 * PDP media gallery (unit M2) — <pdp-gallery>, markup in snippets/pdp-media-gallery.liquid.
 *
 * A native scroll-snap slider and a thumbnail carousel kept in sync both ways:
 *   thumbnail click  -> the main track scrolls to that slide
 *   swipe / trackpad -> the matching thumbnail is marked and kept in view
 *   caret arrows     -> the thumbnail row steps `data-step` thumbnails
 * Media in a slide that is left (video, YouTube, Vimeo) is paused.
 *
 * Vanilla, no dependencies. The element mounts in connectedCallback and cleans up in
 * disconnectedCallback, so a section re-rendered by the theme editor just works.
 *
 * API: el.go(index), el.goToMedia(mediaId), el.next(), el.prev(), el.refresh()
 * Event (bubbles): pdp:gallery:change  detail { index, count, mediaId }
 */
(function () {
  'use strict';

  if (window.__pdpGallery) return;
  window.__pdpGallery = true;

  if (!('customElements' in window) || typeof HTMLElement === 'undefined') return;

  var TOLERANCE = 10; // px: a thumbnail this close to fully visible counts as visible
  var SETTLE_MS = 150; // no scroll event for this long = the scroll has come to rest
  var ARRIVE_PX = 2; // a programmatic scroll this close to its slide has arrived
  var TARGET_MS = 2000; // after this long a programmatic scroll is given up on and the track is followed
  var ANNOUNCE_MS = 2000;
  var reduceQuery = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;

  function reducedMotion() {
    return !!(reduceQuery && reduceQuery.matches);
  }

  function clamp(n, min, max) {
    return Math.max(min, Math.min(max, n));
  }

  function toArray(list) {
    return Array.prototype.slice.call(list || []);
  }

  function scrollX(el, left, instant) {
    var behavior = instant || reducedMotion() ? 'instant' : 'smooth';
    try {
      el.scrollTo({ left: left, behavior: behavior });
    } catch (e) {
      el.scrollLeft = left;
    }
  }

  function pauseMedia(slide) {
    toArray(slide.querySelectorAll('video')).forEach(function (video) {
      try {
        if (!video.paused) video.pause();
      } catch (e) {}
    });

    var host = slide.getAttribute('data-media-host');
    if (!host) return;
    toArray(slide.querySelectorAll('iframe')).forEach(function (frame) {
      var message =
        host === 'youtube'
          ? '{"event":"command","func":"pauseVideo","args":""}'
          : host === 'vimeo'
          ? '{"method":"pause"}'
          : '';
      if (!message) return;
      try {
        if (frame.contentWindow) frame.contentWindow.postMessage(message, '*');
      } catch (e) {}
    });
  }

  class PdpGallery extends HTMLElement {
    connectedCallback() {
      if (this._mounted) return;
      this._mount();
      // Parsed before its children (should not happen with a deferred script): try once more.
      if (!this._mounted && !this._retry) {
        var self = this;
        this._retry = window.requestAnimationFrame(function () {
          self._retry = 0;
          if (self.isConnected && !self._mounted) self._mount();
        });
      }
    }

    disconnectedCallback() {
      this._unmount();
    }

    /* ---------------- set-up / tear-down ---------------- */

    _mount() {
      var list = this.querySelector('[data-pdp-gallery-list]');
      var slides = toArray(this.querySelectorAll('.pdp-gallery__slide'));
      if (!list || !slides.length) return;

      this._mounted = true;
      this._listeners = [];
      this._list = list;
      this._slides = slides;
      this._thumbList = this.querySelector('[data-pdp-gallery-thumb-list]');
      this._thumbItems = toArray(this.querySelectorAll('.pdp-gallery__thumb-item'));
      this._prev = this.querySelector('[data-pdp-gallery-prev]');
      this._next = this.querySelector('[data-pdp-gallery-next]');
      this._status = this.querySelector('[data-pdp-gallery-status]');
      this._step = Math.max(1, parseInt(this.getAttribute('data-step'), 10) || 3);
      this._offsets = [];
      this._width = -1;
      this._target = null; // index a programmatic scroll is heading for
      this._targetAt = 0;
      this._spyFrame = 0;
      this._thumbFrame = 0;
      this._settleTimer = 0;
      this._statusTimer = 0;

      var active = -1;
      slides.forEach(function (slide, i) {
        if (active < 0 && slide.classList.contains('is-active')) active = i;
      });
      this._index = active < 0 ? 0 : active;

      this._variantMedia = null;
      var json = this.querySelector('[data-pdp-gallery-variants]');
      if (json) {
        try {
          this._variantMedia = JSON.parse(json.textContent);
        } catch (e) {
          this._variantMedia = null;
        }
      }

      var self = this;

      this._on(list, 'scroll', function () { self._onListScroll(); }, { passive: true });
      if ('onscrollend' in window) {
        this._on(list, 'scrollend', function () { self._settle(); }, { passive: true });
      }
      // A finger on the track, or a sideways wheel / trackpad scroll, takes over from a
      // programmatic scroll. A vertical wheel is the visitor scrolling the page: ignored.
      this._on(list, 'touchstart', function () { self._target = null; }, { passive: true });
      this._on(list, 'wheel', function (event) {
        if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) self._target = null;
      }, { passive: true });

      if (this._thumbList) {
        this._on(this._thumbList, 'click', function (event) { self._onThumbClick(event); });
        this._on(this._thumbList, 'scroll', function () { self._onThumbScroll(); }, { passive: true });
      }
      if (this._prev) this._on(this._prev, 'click', function () { self._stepThumbs(-1); });
      if (this._next) this._on(this._next, 'click', function () { self._stepThumbs(1); });

      // A variant change (pdp-main.js fires a bubbling `change` on the form's id input).
      if (this._variantMedia) {
        var root = this.closest('[data-pdp-main]') || this.closest('.shopify-section');
        if (root) this._on(root, 'change', function (event) { self._onVariantChange(event); });
      }

      if ('ResizeObserver' in window) {
        this._resizeObserver = new ResizeObserver(function () { self.refresh(); });
        this._resizeObserver.observe(list);
        if (this._thumbList) this._resizeObserver.observe(this._thumbList);
      } else {
        this._on(window, 'resize', function () { self.refresh(); }, { passive: true });
      }

      this._measure();
      this._updateArrows();
      this.classList.add('is-ready');

      var initial = this.getAttribute('data-initial-media');
      var opened = initial ? this.goToMedia(initial, { instant: true, silent: true }) : false;
      if (!opened) {
        // A browser may restore the track's scroll position on reload: follow it.
        var page = this._pageFromScroll();
        if (page !== this._index) this._setActive(page, { announce: false, instantThumbs: true });
      }
    }

    _unmount() {
      if (this._retry) {
        window.cancelAnimationFrame(this._retry);
        this._retry = 0;
      }
      if (!this._mounted) return;
      this._mounted = false;

      (this._listeners || []).forEach(function (l) {
        l[0].removeEventListener(l[1], l[2], l[3]);
      });
      this._listeners = [];

      if (this._resizeObserver) {
        this._resizeObserver.disconnect();
        this._resizeObserver = null;
      }
      if (this._spyFrame) window.cancelAnimationFrame(this._spyFrame);
      if (this._thumbFrame) window.cancelAnimationFrame(this._thumbFrame);
      window.clearTimeout(this._settleTimer);
      window.clearTimeout(this._statusTimer);
      this._spyFrame = this._thumbFrame = this._settleTimer = this._statusTimer = 0;
      this.classList.remove('is-ready');
    }

    _on(target, type, handler, options) {
      target.addEventListener(type, handler, options || false);
      this._listeners.push([target, type, handler, options || false]);
    }

    /* ---------------- public API ---------------- */

    go(index, options) {
      if (!this._mounted) return;
      options = options || {};
      index = clamp(parseInt(index, 10) || 0, 0, this._slides.length - 1);

      this._measure();
      this._setActive(index, { announce: !options.silent, instantThumbs: options.instant });

      // Until the track arrives the scroll-spy stays quiet, or the marked thumbnail would
      // walk through every slide on the way (or jump back if the scroll is slow to start).
      this._target = index;
      this._targetAt = Date.now();
      scrollX(this._list, this._offsets[index] || 0, options.instant);
      this._armSettle();
    }

    goToMedia(mediaId, options) {
      if (!this._mounted || mediaId == null) return false;
      var id = String(mediaId);
      var index = -1;
      this._slides.forEach(function (slide, i) {
        if (index > -1) return;
        var own = slide.getAttribute('data-media-id') || '';
        // Accepts "<section>-<media>" as printed, or a bare media id.
        if (own === id || own.slice(-(id.length + 1)) === '-' + id) index = i;
      });
      if (index < 0) return false;
      this.go(index, options);
      return true;
    }

    next() {
      this.go(this._index + 1);
    }

    prev() {
      this.go(this._index - 1);
    }

    refresh() {
      if (!this._mounted) return;
      var resized = this._list.clientWidth !== this._width;
      this._measure();
      // A new width (or the 0 -> 40px slide gap at 750px) leaves the track between two
      // slides, or on another one: put the current slide back, at once.
      if (resized) {
        var left = this._offsets[this._index] || 0;
        if (Math.abs(this._list.scrollLeft - left) > 1) scrollX(this._list, left, true);
      }
      this._updateArrows();
    }

    /* ---------------- main track ---------------- */

    _measure() {
      this._width = this._list.clientWidth;
      this._offsets = this._slides.map(function (slide) {
        return slide.offsetLeft;
      });
      // The thumbnail row snaps `scroll-padding-left` short of a thumbnail's own edge.
      this._thumbPad = 0;
      if (this._thumbList) {
        try {
          this._thumbPad = parseFloat(window.getComputedStyle(this._thumbList).scrollPaddingLeft) || 0;
        } catch (e) {
          this._thumbPad = 0;
        }
      }
    }

    _pageFromScroll() {
      var left = this._list.scrollLeft;
      var best = 0;
      var distance = Infinity;
      for (var i = 0; i < this._offsets.length; i++) {
        var d = Math.abs(this._offsets[i] - left);
        if (d < distance) {
          distance = d;
          best = i;
        }
      }
      return best;
    }

    _arrived() {
      if (this._target == null) return true;
      return Math.abs(this._list.scrollLeft - (this._offsets[this._target] || 0)) <= ARRIVE_PX;
    }

    _onListScroll() {
      // The browser re-snaps the track when its width changes, before any observer has
      // run: that is a resize, not a swipe, and the old offsets would name the wrong slide.
      if (this._list.clientWidth !== this._width) {
        this.refresh();
        return;
      }
      this._armSettle();
      if (this._target != null) {
        if (this._arrived()) this._target = null;
        return;
      }
      if (this._spyFrame) return;
      var self = this;
      this._spyFrame = window.requestAnimationFrame(function () {
        self._spyFrame = 0;
        if (!self._mounted || self._target != null || self._list.clientWidth !== self._width) return;
        var page = self._pageFromScroll();
        if (page !== self._index) self._setActive(page, { announce: false });
      });
    }

    _armSettle() {
      var self = this;
      window.clearTimeout(this._settleTimer);
      this._settleTimer = window.setTimeout(function () { self._settle(); }, SETTLE_MS);
    }

    _settle() {
      window.clearTimeout(this._settleTimer);
      this._settleTimer = 0;
      if (!this._mounted) return;
      if (this._list.clientWidth !== this._width) {
        this.refresh();
        return;
      }
      if (this._target != null) {
        // Not there yet (a slow start, or one smooth scroll replaced by the next): keep waiting.
        if (!this._arrived() && Date.now() - this._targetAt < TARGET_MS) {
          this._armSettle();
          return;
        }
        this._target = null;
      }
      var page = this._pageFromScroll();
      if (page !== this._index) this._setActive(page, { announce: false });
    }

    _setActive(index, options) {
      options = options || {};
      var changed = index !== this._index;
      this._index = index;

      this._slides.forEach(function (slide, i) {
        var on = i === index;
        slide.classList.toggle('is-active', on);
        if (!on) pauseMedia(slide);
      });

      this._thumbItems.forEach(function (item, i) {
        var button = item.querySelector('.pdp-gallery__thumb');
        if (!button) return;
        if (i === index) button.setAttribute('aria-current', 'true');
        else button.removeAttribute('aria-current');
      });

      this._revealThumb(index, options.instantThumbs);
      if (options.announce) this._announce(index + 1);

      if (changed) {
        var slide = this._slides[index];
        this.dispatchEvent(
          new CustomEvent('pdp:gallery:change', {
            bubbles: true,
            detail: {
              index: index,
              count: this._slides.length,
              mediaId: slide ? slide.getAttribute('data-media-id') : null
            }
          })
        );
      }
    }

    /* ---------------- thumbnails ---------------- */

    _onThumbClick(event) {
      var button = event.target && event.target.closest ? event.target.closest('.pdp-gallery__thumb') : null;
      if (!button || !this.contains(button)) return;
      var item = button.closest('.pdp-gallery__thumb-item');
      var index = this._thumbItems.indexOf(item);
      if (index < 0) return;

      var target = item.getAttribute('data-target');
      if (target && this.goToMedia(target)) return;
      this.go(index);
    }

    _revealThumb(index, instant) {
      var list = this._thumbList;
      var item = this._thumbItems[index];
      if (!list || !item) return;

      var left = item.offsetLeft;
      var right = left + item.offsetWidth;
      var viewLeft = list.scrollLeft;
      var viewRight = viewLeft + list.clientWidth;
      if (left >= viewLeft - TOLERANCE && right <= viewRight + TOLERANCE) return;

      // Aim at the thumbnail's own snap position, so the mandatory snap has nothing to correct.
      scrollX(list, Math.max(0, left - (this._thumbPad || 0)), instant);
    }

    _thumbPitch() {
      var items = this._thumbItems;
      if (items.length > 1) return items[1].offsetLeft - items[0].offsetLeft;
      return items.length ? items[0].offsetWidth : 0;
    }

    _stepThumbs(direction) {
      var list = this._thumbList;
      if (!list) return;
      var max = Math.max(0, list.scrollWidth - list.clientWidth);
      var left = clamp(list.scrollLeft + direction * this._step * this._thumbPitch(), 0, max);
      scrollX(list, left, false);
    }

    _onThumbScroll() {
      if (this._thumbFrame) return;
      var self = this;
      this._thumbFrame = window.requestAnimationFrame(function () {
        self._thumbFrame = 0;
        if (self._mounted) self._updateArrows();
      });
    }

    _updateArrows() {
      var list = this._thumbList;
      var items = this._thumbItems;
      if (!list || !items.length) return;

      var last = items[items.length - 1];
      var left = list.scrollLeft;
      var atStart = left <= 1;
      var atEnd = last.offsetLeft + last.offsetWidth <= left + list.clientWidth + TOLERANCE;

      this._toggleArrow(this._prev, atStart, this._next);
      this._toggleArrow(this._next, atEnd, this._prev);
    }

    _toggleArrow(arrow, disabled, other) {
      if (!arrow || arrow.disabled === disabled) return;
      // A disabled button drops focus: hand it to the other arrow so the keyboard keeps its place.
      var hadFocus = disabled && document.activeElement === arrow;
      arrow.disabled = disabled;
      if (hadFocus && other && !other.disabled) {
        try {
          other.focus({ preventScroll: true });
        } catch (e) {
          other.focus();
        }
      }
    }

    /* ---------------- variants / live region ---------------- */

    _onVariantChange(event) {
      var input = event.target;
      if (!input || !this._variantMedia) return;
      var isVariantInput =
        input.name === 'id' || (input.hasAttribute && input.hasAttribute('data-pdp-variant-id'));
      if (!isVariantInput) return;
      var mediaId = this._variantMedia[String(input.value)];
      if (mediaId) this.goToMedia(mediaId, { silent: true });
    }

    _announce(position) {
      var status = this._status;
      if (!status) return;
      var template = this.getAttribute('data-announce') || 'Image [index] is now available in gallery view';
      status.setAttribute('aria-hidden', 'false');
      status.textContent = template.replace('[index]', position);
      window.clearTimeout(this._statusTimer);
      this._statusTimer = window.setTimeout(function () {
        status.setAttribute('aria-hidden', 'true');
      }, ANNOUNCE_MS);
    }
  }

  try {
    if (!window.customElements.get('pdp-gallery')) {
      window.customElements.define('pdp-gallery', PdpGallery);
    }
  } catch (e) {}
})();
